import {
  AdditionalContributionIcon,
  ConsentOrdersSpecialPaymentSchema,
  InitialDetails,
  RadioSelect,
  TextArea
} from "@fla/components";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";

interface IProps {
  formId: string;
  store: InitialDetails;
  onSubmit: (values: InitialDetails) => void;
}

export default function SpecialFinancialPayment({ onSubmit, formId, store }: IProps) {
  const { handleSubmit, watch, control } = useForm({
    mode: "all",
    defaultValues: store,
    resolver: yupResolver(ConsentOrdersSpecialPaymentSchema)
  });

  return (
    <form id={formId} className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
      <div className="mb-8 pb-8 border-b">
        <AdditionalContributionIcon classes="w-14 h-14 mx-auto mb-5" />

        <h3 className="text-center">Additional Financial Contributions</h3>
      </div>

      <p className="text-lg font-bold">Did you or {store.spouse.first_name} receive any of the following?</p>

      <div className="bg-[#f2f2f2] p-2 md:p-5 rounded-lg space-y-3">
        <RadioSelect
          title="Significant gifts from parent(s)"
          control={control}
          name="contributions.significant_gifts"
          id="contributions.significant_gifts"
          options={["yes", "no"]}
          theme="dark"
        />
        {watch("contributions.significant_gifts") === "yes" ? (
          <TextArea
            title="Give details of gifts from parent(s)"
            control={control}
            name="contributions.significant_gifts_details"
            id="contributions.significant_gifts_details"
            placeholder="details"
            theme="dark"
          />
        ) : null}
      </div>

      {/*  */}
      <div className="bg-[#f2f2f2] p-2 md:p-5 rounded-lg space-y-3">
        <RadioSelect
          title="Inheritances"
          control={control}
          name="contributions.inheritance"
          id="contributions.inheritance"
          options={["yes", "no"]}
          theme="dark"
        />
        {watch("contributions.inheritance") === "yes" ? (
          <TextArea
            title="Give details of inheritances"
            control={control}
            name="contributions.inheritance_details"
            id="contributions.inheritance_details"
            placeholder="details"
            theme="dark"
          />
        ) : null}
      </div>

      {/*  */}
      <div className="bg-[#f2f2f2] p-2 md:p-5 rounded-lg space-y-3">
        <RadioSelect
          title="Personal injury claim funds"
          control={control}
          name="contributions.personal_injury"
          id="contributions.personal_injury"
          options={["yes", "no"]}
          theme="dark"
        />
        {watch("contributions.personal_injury") === "yes" ? (
          <TextArea
            title="Give details of personal injury payments"
            control={control}
            name="contributions.personal_injury_details"
            id="contributions.personal_injury_details"
            placeholder="details"
            theme="dark"
          />
        ) : null}
      </div>

      {/*  */}
      <div className="bg-[#f2f2f2] p-2 md:p-5 rounded-lg space-y-3">
        <RadioSelect
          title="Workers Compensation"
          control={control}
          name="contributions.workers_comp"
          id="contributions.workers_comp"
          options={["yes", "no"]}
          theme="dark"
        />
        {watch("contributions.workers_comp") === "yes" ? (
          <TextArea
            title="Give details of workers compensation"
            control={control}
            name="contributions.workers_comp_details"
            id="contributions.workers_comp_details"
            placeholder="details"
            theme="dark"
          />
        ) : null}
      </div>

      {/*  */}
      <div className="bg-[#f2f2f2] p-2 md:p-5 rounded-lg space-y-3">
        <RadioSelect
          title="Redundancy payments"
          control={control}
          name="contributions.redundancy"
          id="contributions.redundancy"
          options={["yes", "no"]}
          theme="dark"
        />
        {watch("contributions.redundancy") === "yes" ? (
          <TextArea
            title="Give details of redundancy"
            control={control}
            name="contributions.redundancy_details"
            id="contributions.redundancy_details"
            placeholder="details"
            theme="dark"
          />
        ) : null}
      </div>

      {/*  */}
      <div className="bg-[#f2f2f2] p-2 md:p-5 rounded-lg space-y-3">
        <RadioSelect
          title="Winnings"
          subTitle="E.g. Any significant lottery, betting or competition winnings"
          control={control}
          name="contributions.winnings"
          id="contributions.winnings"
          options={["yes", "no"]}
          theme="dark"
        />
        {watch("contributions.winnings") === "yes" ? (
          <TextArea
            title="Give details of winnings"
            control={control}
            name="contributions.winnings_details"
            id="contributions.winnings_details"
            placeholder="details"
            theme="dark"
          />
        ) : null}
      </div>

      <div className="bg-[#f2f2f2] p-2 md:p-5 rounded-lg space-y-3">
        <RadioSelect
          title="Payments for the welfare of other party/children"
          control={control}
          name="contributions.welfare_other_children"
          id="contributions.welfare_other_children"
          options={["yes", "no"]}
          theme="dark"
        />
        {watch("contributions.welfare_other_children") === "yes" ? (
          <TextArea
            title="Give details of payments for the welfare"
            control={control}
            name="contributions.welfare_other_children_details"
            id="contributions.welfare_other_children_details"
            placeholder="details"
            theme="dark"
          />
        ) : null}
      </div>

      <div className="bg-[#f2f2f2] p-2 md:p-5 rounded-lg space-y-3">
        <RadioSelect
          title="Other significant financial payments"
          subTitle="E.g. Any other money coming in to the relationship not from income"
          control={control}
          name="contributions.other_payments"
          id="contributions.other_payments"
          options={["yes", "no"]}
          theme="dark"
        />
        {watch("contributions.other_payments") === "yes" ? (
          <TextArea
            title="Give details of significant financial payments"
            control={control}
            name="contributions.other_payments_details"
            id="contributions.other_payments_details"
            placeholder="details"
            theme="dark"
          />
        ) : null}
      </div>
    </form>
  );
}
